/**
 * Unified attributes — the pure core of cross-app attribute unification.
 *
 * A unified attribute says: THIS Miro signifier, THIS Notion property and THIS
 * Jira field are the same canonical attribute, each seen through its own value
 * map. This module holds the declaration types, both translation directions,
 * the canonical-field ⇄ item-patch mapping, loop-protection keys, and the
 * inbound planner. No I/O — see unifiedAttributesService for persistence and
 * the runtime hooks.
 *
 * Spec: docs/9-exploration/semantic-connectors/07-VISION-attribute-unification.md
 *       (migration 720 records the owner rulings this slice takes on §5).
 */

import {
  INTERNAL_FIELDS,
  dimensionsEquivalent,
  type InternalFieldDef,
  type SignifierDimension,
} from '../fieldMappingRegistry';
import type { SemanticObject } from './snapshot';

// ── Declaration types ─────────────────────────────────────────────────────────

export type UnifiedPropagationMode = 'inbound' | 'bidirectional';
export type UnifiedConflictPolicy = 'source_wins' | 'canonical_wins' | 'review';
export type BindingSourceKind = 'field' | 'signifier';

export interface ValueMapEntry {
  externalValue: string;
  internalValue: string;
}

export interface UnifiedAttribute {
  id: string;
  workspaceId: string;
  /** Internal field key: 'title' | 'description' | 'status' | 'category' | 'altitude' | 'attribute:<k>'. */
  canonicalFieldKey: string;
  name: string;
  description: string | null;
  propagation: UnifiedPropagationMode;
  conflictPolicy: UnifiedConflictPolicy;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UnifiedAttributeBinding {
  id: string;
  unifiedAttributeId: string;
  workspaceId: string;
  connectorType: string;
  sourceKind: BindingSourceKind;
  /** SignifierDimension token, dialect field key, or routed attribute key. */
  sourceKey: string;
  /** Empty = pass-through. */
  valueMap: ValueMapEntry[];
  /** internalValue → externalValue for one-to-many outbound. */
  outboundDefaults: Record<string, string>;
  enabled: boolean;
}

export interface UnifiedAttributeWithBindings extends UnifiedAttribute {
  bindings: UnifiedAttributeBinding[];
}

export type PropagationDirection = 'inbound' | 'outbound';
export type PropagationStatus =
  | 'applied'
  | 'queued'
  | 'dispatched'
  | 'skipped'
  | 'failed'
  | 'review'
  | 'dismissed';

export const PROPAGATION_MODE_LABELS: Readonly<Record<UnifiedPropagationMode, string>> = {
  inbound: 'Inbound only',
  bidirectional: 'Bidirectional',
};

export const CONFLICT_POLICY_LABELS: Readonly<Record<UnifiedConflictPolicy, string>> = {
  source_wins: 'Connected app wins',
  canonical_wins: 'Canonical wins (fill blanks only)',
  review: 'Hold for review',
};

// ── Which canonical fields can be unified ─────────────────────────────────────

const UNIFIABLE_CATEGORIES = new Set<InternalFieldDef['category']>([
  'standard',
  'category',
  'altitude',
  'attribute',
]);

/** Internal fields a unified attribute may target (the ones with an item writer below). */
export function unifiableInternalFields(): InternalFieldDef[] {
  return INTERNAL_FIELDS.filter((f) => UNIFIABLE_CATEGORIES.has(f.category));
}

export function isUnifiableFieldKey(key: string): boolean {
  return key.startsWith('attribute:') || unifiableInternalFields().some((f) => f.key === key);
}

// ── Canonical field ⇄ item ─────────────────────────────────────────────────────

/** The subset of an item this module reads and writes. */
export interface CanonicalItemView {
  title?: string;
  description?: string;
  exit_status?: string;
  itemCategory?: string;
  altitudeLevel?: number | string | null;
  metadata?: Record<string, unknown>;
}

function coerceNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Read the canonical value for a field key from an item. */
export function readCanonicalValue(item: CanonicalItemView, key: string): unknown {
  switch (key) {
    case 'title':
      return item.title ?? undefined;
    case 'description':
      return item.description ?? undefined;
    case 'status':
      return item.exit_status ?? undefined;
    case 'category':
      return item.itemCategory ?? undefined;
    case 'altitude':
      return item.altitudeLevel ?? undefined;
    default:
      if (key.startsWith('attribute:')) return item.metadata?.[key];
      return undefined;
  }
}

const STATUS_VALUES = new Set(['active', 'archived', 'deferred']);

/**
 * Turn a canonical value into the item patch that writes it. Returns null when
 * the value cannot be written honestly (unknown status, non-numeric altitude).
 * Attribute keys land in `metadata['attribute:<k>']`, the whiteboard-import
 * precedent (whiteboardSignifiers.assignmentsToItemPatch).
 */
export function canonicalValueToItemPatch(
  key: string,
  value: unknown,
  currentMetadata?: Record<string, unknown>,
): Record<string, unknown> | null {
  switch (key) {
    case 'title':
      return typeof value === 'string' && value.trim() ? { title: value } : null;
    case 'description':
      return typeof value === 'string' ? { description: value } : null;
    case 'status':
      return typeof value === 'string' && STATUS_VALUES.has(value) ? { exit_status: value } : null;
    case 'category':
      return typeof value === 'string' && value ? { itemCategory: value } : null;
    case 'altitude': {
      const n = coerceNumber(value);
      return n === null ? null : { altitudeLevel: n };
    }
    default: {
      if (!key.startsWith('attribute:')) return null;
      const n = coerceNumber(value);
      return { metadata: { ...(currentMetadata ?? {}), [key]: n ?? value } };
    }
  }
}

/** Map an items-store field-change record onto canonical keys. */
export function itemFieldChangesToCanonical(
  fieldChanges: Record<string, { from: unknown; to: unknown }>,
): Array<{ canonicalKey: string; from: unknown; to: unknown }> {
  const out: Array<{ canonicalKey: string; from: unknown; to: unknown }> = [];
  const direct: Record<string, string> = {
    title: 'title',
    description: 'description',
    exit_status: 'status',
    itemCategory: 'category',
    altitudeLevel: 'altitude',
  };
  for (const [field, change] of Object.entries(fieldChanges)) {
    if (direct[field]) {
      out.push({ canonicalKey: direct[field], from: change.from, to: change.to });
      continue;
    }
    if (field === 'metadata') {
      const before = (change.from as Record<string, unknown> | undefined) ?? {};
      const after = (change.to as Record<string, unknown> | undefined) ?? {};
      const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
      for (const k of keys) {
        if (!k.startsWith('attribute:')) continue;
        if (before[k] !== after[k]) out.push({ canonicalKey: k, from: before[k], to: after[k] });
      }
    }
  }
  return out;
}

// ── Translation ───────────────────────────────────────────────────────────────

function norm(v: unknown): string {
  return String(v ?? '').trim().toLowerCase();
}

/**
 * Dialect value → canonical value through the binding's value map.
 * Empty map = pass-through. No match = undefined (the hop is recorded as
 * skipped: unmapped value, never guessed).
 */
export function translateInbound(binding: UnifiedAttributeBinding, raw: unknown): unknown {
  if (raw === undefined || raw === null || raw === '') return undefined;
  if (binding.valueMap.length === 0) return raw;
  const hit = binding.valueMap.find((e) => norm(e.externalValue) === norm(raw));
  return hit ? hit.internalValue : undefined;
}

export type OutboundTranslation =
  | { ok: true; value: string }
  | { ok: false; reason: string };

/**
 * Canonical value → dialect value. One canonical value may map to several
 * dialect values (Miro has ~16 colours, impact has 3–5 levels): then the
 * binding's declared default decides, or the hop is refused as lossy.
 */
export function translateOutbound(binding: UnifiedAttributeBinding, canonical: unknown): OutboundTranslation {
  if (canonical === undefined || canonical === null || canonical === '') {
    return { ok: false, reason: 'canonical value is empty' };
  }
  if (binding.valueMap.length === 0) return { ok: true, value: String(canonical) };
  const candidates = binding.valueMap.filter((e) => norm(e.internalValue) === norm(canonical));
  if (candidates.length === 1) return { ok: true, value: candidates[0].externalValue };
  if (candidates.length === 0) {
    return { ok: false, reason: `no ${binding.connectorType} value is mapped to "${String(canonical)}"` };
  }
  const declared = binding.outboundDefaults[String(canonical)] ?? binding.outboundDefaults[norm(canonical)];
  if (declared && candidates.some((c) => norm(c.externalValue) === norm(declared))) {
    return { ok: true, value: declared };
  }
  return {
    ok: false,
    reason: `"${String(canonical)}" maps to ${candidates.length} ${binding.connectorType} values (${candidates
      .map((c) => c.externalValue)
      .join(', ')}) — declare an outbound default`,
  };
}

// ── Reading a dialect object ──────────────────────────────────────────────────

/** What a binding sees on a semantic object; undefined = the object carries no such attribute. */
export function readSourceValue(object: SemanticObject, binding: UnifiedAttributeBinding): unknown {
  if (binding.sourceKind === 'signifier') {
    const want = binding.sourceKey as SignifierDimension;
    for (const [dim, value] of Object.entries(object.visual)) {
      if (value === undefined) continue;
      if (dim === want || dimensionsEquivalent(dim as SignifierDimension, want)) return value;
    }
    return undefined;
  }
  if (binding.sourceKey === 'title') return object.content.text;
  if (binding.sourceKey === 'description') return object.content.description;
  const asserted = object.asserted.attributes?.[binding.sourceKey];
  return asserted?.value;
}

/** Every attribute a dialect object carries, for the at-rest ledger. */
export function extractSourceAttributes(
  object: SemanticObject,
): Array<{ sourceKind: BindingSourceKind; sourceKey: string; rawValue: unknown }> {
  const out: Array<{ sourceKind: BindingSourceKind; sourceKey: string; rawValue: unknown }> = [];
  for (const [dim, value] of Object.entries(object.visual)) {
    if (value !== undefined && value !== null && value !== '') {
      out.push({ sourceKind: 'signifier', sourceKey: dim, rawValue: value });
    }
  }
  out.push({ sourceKind: 'field', sourceKey: 'title', rawValue: object.content.text });
  if (object.content.description) {
    out.push({ sourceKind: 'field', sourceKey: 'description', rawValue: object.content.description });
  }
  for (const [key, assertion] of Object.entries(object.asserted.attributes ?? {})) {
    out.push({ sourceKind: 'field', sourceKey: key, rawValue: assertion.value });
  }
  return out;
}

// ── Loop protection ───────────────────────────────────────────────────────────

/** Stable key for "this value, on this field, on this item" — echo suppression. */
export function echoKey(itemId: string, canonicalKey: string, value: unknown): string {
  return `${itemId}::${canonicalKey}::${norm(value)}`;
}

// ── Outbound transport fidelity ───────────────────────────────────────────────

/**
 * Which item fields the change_events → dispatch → engine transport can carry
 * per connector today (supabase/functions/_shared/fieldSyncOutbound.ts
 * OUTBOUND_ITEM_FIELDS and the engine's /jira/items/update and
 * /linear/issues/update). Anything else is recorded as skipped with this
 * reason — declared, not dropped. Widening a dispatcher widens this table in
 * the same PR. Status does not go out: it belongs to the tool the work is done in.
 */
export const OUTBOUND_TRANSPORT: Readonly<Record<string, ReadonlySet<string>>> = {
  jira: new Set(['title', 'description', 'tags']),
  linear: new Set(['title', 'description']),
  notion: new Set(['title']),
};

/**
 * Which item fields an inbound change from each connector can write today, after the webhook
 * names the field and /apply turns it into an item column. Jira's summary and labels arrive as
 * the title and tags; Notion sends page-level events with no field, so nothing is listed for it.
 */
// reuse-ack: mirrors supabase/functions/apply/index.ts ENTITY_TABLE_MAP.item — an edge function cannot import from src/; widen both together.
export const INBOUND_TRANSPORT: Readonly<Record<string, ReadonlySet<string>>> = {
  jira: new Set(['title', 'description', 'tags']),
  linear: new Set(['title', 'description']),
};

export function outboundCarriable(connectorType: string, canonicalKey: string): { ok: true } | { ok: false; reason: string } {
  const set = OUTBOUND_TRANSPORT[connectorType];
  if (!set) return { ok: false, reason: `no outbound transport for ${connectorType} yet` };
  if (!set.has(canonicalKey)) {
    return { ok: false, reason: `the ${connectorType} dispatcher cannot carry "${canonicalKey}" yet` };
  }
  return { ok: true };
}

// ── Inbound planner ───────────────────────────────────────────────────────────

export interface InboundPlanRow {
  itemId: string;
  unifiedAttributeId: string;
  bindingId: string;
  canonicalKey: string;
  connectorType: string;
  rawValue: unknown;
  value: unknown;
  currentValue: unknown;
  action: 'apply' | 'review' | 'skip' | 'noop';
  reason?: string;
}

/**
 * Plan inbound hops for a batch of dialect objects that now correspond to
 * platform items. Pure: the caller supplies current canonical values.
 */
export function planInbound(params: {
  connectorType: string;
  objects: Array<{ object: SemanticObject; itemId: string }>;
  attributes: readonly UnifiedAttributeWithBindings[];
  currentValue: (itemId: string, canonicalKey: string) => unknown;
}): InboundPlanRow[] {
  const rows: InboundPlanRow[] = [];
  for (const attr of params.attributes) {
    if (!attr.active) continue;
    const bindings = attr.bindings.filter((b) => b.enabled && b.connectorType === params.connectorType);
    if (bindings.length === 0) continue;
    for (const { object, itemId } of params.objects) {
      for (const binding of bindings) {
        const raw = readSourceValue(object, binding);
        if (raw === undefined || raw === null || raw === '') continue;
        const value = translateInbound(binding, raw);
        const current = params.currentValue(itemId, attr.canonicalFieldKey);
        const base = {
          itemId,
          unifiedAttributeId: attr.id,
          bindingId: binding.id,
          canonicalKey: attr.canonicalFieldKey,
          connectorType: binding.connectorType,
          rawValue: raw,
          value,
          currentValue: current,
        };
        if (value === undefined) {
          rows.push({ ...base, action: 'skip', reason: `"${String(raw)}" is not in the value map` });
          continue;
        }
        if (norm(value) === norm(current)) {
          rows.push({ ...base, action: 'noop', reason: 'already equal' });
          continue;
        }
        const hasCurrent = current !== undefined && current !== null && current !== '';
        if (hasCurrent && attr.conflictPolicy === 'canonical_wins') {
          rows.push({ ...base, action: 'skip', reason: 'canonical wins — value already set' });
          continue;
        }
        if (hasCurrent && attr.conflictPolicy === 'review') {
          rows.push({ ...base, action: 'review', reason: 'differs from canonical — held for review' });
          continue;
        }
        rows.push({ ...base, action: 'apply' });
      }
    }
  }
  return rows;
}
